import {Box, IconButton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from '@mui/material';
import {InfoOutlined, ListAltOutlined, Refresh} from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {ChangeEvent, FC, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import {ocppApi} from 'src/api';
import {useAppSelector} from 'src/app/hooks';
import {OcppLogCard} from 'src/components/chargerLogs/OcppLogCard';
import {DateSelect} from 'src/components/common/DateSelect';
import {PageHeader} from 'src/components/common/PageHeader';
import {TableLayout} from 'src/components/tables/TableLayout';
import {TimeMapEnum} from 'src/components/forms/formData/common/initialData';
import {OcppLogsSearchData, PageOcppLogDTO} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {
    dateWrapperSx,
    emptyStateSx,
    iconBtnSx,
    rootSx,
    sortToggleGroupSx,
    stackSx,
    titleNoteSx,
    toolbarSx,
} from './ChargerLogs.sx';

dayjs.extend(utc);

type LogsTrigger = ReturnType<typeof ocppApi.useLazySearchOcppLogsQuery>[0];

const runSearch = (trigger: LogsTrigger) =>
    async (data: OcppLogsSearchData, size: number, page: number, sortAsc: boolean) => {
        try {
            return await trigger({
                data: {...data, sortAsc},
                params: {
                    size,
                    page,
                    sort: `occurredAt,${sortAsc ? 'asc' : 'desc'}`,
                },
            }).unwrap();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        }
    };

export const ChargerLogs: FC = () => {
    const {id} = useParams();
    const intl = useIntl();
    const {activeBusiness} = useAppSelector((state) => state.common);

    const [trigger, response] = ocppApi.useLazySearchOcppLogsQuery();

    const [perPage, setPerPage] = useState(100);
    const [currentPage, setCurrentPage] = useState(0);
    const [sortAsc, setSortAsc] = useState(false);
    const [dates, setDates] = useState<{
        logs: { from: number; to: number };
    }>({
        logs: {
            from: dayjs.utc().subtract(1, 'week').valueOf(),
            to: dayjs.utc().valueOf(),
        },
    });

    const [storedData, setStoredData] = useState<PageOcppLogDTO | undefined>(undefined);
    useEffect(() => {
        if (response.data) {
            setStoredData(response.data);
        }
    }, [response.data]);

    const handleChangeDates = (newFrom: number, newTo: number, field: string) => () => {
        setDates({[field]: {from: newFrom, to: newTo}} as typeof dates);
        setCurrentPage(0);
    };

    const handlePerPageChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const val = +e.target.value;
        if (typeof val === 'number') {
            setPerPage(val);
            setCurrentPage(0);
        }
    };

    const handleChangePage = (page: number) => {
        setCurrentPage(page);
    };

    const handleChangeSort = (_e: unknown, value: 'asc' | 'desc' | null) => {
        if (value === null) {
            return;
        }
        setSortAsc(value === 'asc');
        setCurrentPage(0);
    };

    const loadLogs = () => {
        if (!id) {
            return;
        }
        void runSearch(trigger)(
            {
                businessId: activeBusiness,
                chargerId: +id,
                from: dates.logs.from,
                to: dates.logs.to,
            },
            perPage,
            currentPage,
            sortAsc,
        );
    };

    useEffect(() => {
        loadLogs();
    }, [dates, perPage, currentPage, sortAsc]);

    const logs = storedData?.content ?? [];
    const totalPages = storedData?.page?.totalPages ?? 0;
    const hasItems = logs.length > 0;

    const retentionNote = (
        <Tooltip title={intl.formatMessage({id: 'ocppLogsRetentionTooltip'})}>
            <Box component="span" sx={titleNoteSx}>
                <InfoOutlined/>
                <FormattedMessage id="ocppLogsRetentionShort"/>
            </Box>
        </Tooltip>
    );

    return (
        <Box sx={rootSx}>
            <PageHeader
                title={intl.formatMessage({id: 'ocppLogsTitle'})}
                onlyTitle
                icon={<ListAltOutlined/>}
                titleSuffix={retentionNote}
            >
                <Box sx={toolbarSx}>
                    <Box sx={dateWrapperSx}>
                        <DateSelect
                            handleTimeChange={handleChangeDates}
                            field="logs"
                            currentTimeInit={TimeMapEnum.LAST_WEEK}
                        />
                    </Box>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={sortAsc ? 'asc' : 'desc'}
                        onChange={handleChangeSort}
                        sx={sortToggleGroupSx}
                    >
                        <ToggleButton value="desc">
                            <FormattedMessage id="ocppLogsSortNewestFirst"/>
                        </ToggleButton>
                        <ToggleButton value="asc">
                            <FormattedMessage id="ocppLogsSortOldestFirst"/>
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Tooltip title={intl.formatMessage({id: 'ocppLogsRefresh'})}>
                        <IconButton
                            onClick={loadLogs}
                            sx={iconBtnSx}
                            aria-label={intl.formatMessage({id: 'ocppLogsRefresh'})}
                        >
                            <Refresh/>
                        </IconButton>
                    </Tooltip>
                </Box>
            </PageHeader>
            <TableLayout
                hideFooter={!hasItems}
                totalPages={totalPages}
                page={currentPage}
                pageSize={perPage}
                onPageChange={handleChangePage}
                onPageSizeChange={handlePerPageChange}
            >
                <Stack sx={stackSx}>
                    {!hasItems && !response.isFetching && (
                        <Box sx={emptyStateSx}>
                            <Typography variant="subtitle1">
                                <FormattedMessage id="ocppLogsEmpty"/>
                            </Typography>
                        </Box>
                    )}
                    {logs.map((log) => (
                        <OcppLogCard key={log.id} log={log}/>
                    ))}
                </Stack>
            </TableLayout>
        </Box>
    );
};
